import { createStore } from 'vuex'

const isAndroid = () => /Android/i.test(navigator.userAgent)
const isIOS = () => /iPhone|iPad|iPod|iOS/i.test(navigator.userAgent)

export default createStore({
    state: {
        deviceVersion: '',
        device: '',
        sessionToken: null,
        sessionKey: null,
        sessionTuitionPayment: false,
        loading: false,
        loadingMessage: '',
        lockBack: false,
        isTuitionFeeAgreementChecked: false,
        isPublicBuildingFeeAgreementChecked: false,
        supportFeatures: {
            tuition: true,
            localtax: true,
            scanningv2: true,
            scanningv3: true,
            withSupportPaymentMethod: true,
            supportCarrierNumber: true,
            supportCarrierSetting: true,
            supportTigerYear: true,
            supportCardlessBinding: true,
            supportBindingVehicle: true,
        },
        localTaxList: null,
        carrier: {
            carrierType: 'MOBILE',
            carrierNumber: null,
        },
        readyToPayBill: null,
        studentInformation: null,
        studentIdNumber: null,
    },
    mutations: {
        SENT_APP(state, sent) {
            try {
                const data = JSON.parse(JSON.stringify({ action: sent.action, payload: sent.payload }))

                if (isAndroid()) {
                    // eslint-disable-next-line no-undef
                    app.android(JSON.stringify(data))
                } else if (isIOS()) {
                    window.webkit.messageHandlers.ios.postMessage(data)
                }
            } catch (e) {
                console.error(`[nativeBridge] Failed to send action: ${sent && sent.action}`, e)
            }
        },
        GET_DEVICE_INFORMATION(state) {
            const ua = navigator.userAgent || ''
            state.deviceVersion = (ua.split('/')[1] || '').split(' ')[0]
            if (isAndroid()) {
                state.device = 'Android'
            } else if (isIOS()) {
                state.device = 'iOS'
            } else {
                state.device = 'desktop'
            }
        },
        SET_SESSION(state, session) {
            if (session.sessionKey != null) {
                state.sessionKey = session.sessionKey
                sessionStorage.setItem('sessionKey', session.sessionKey)

                state.sessionToken = session.sessionToken
                sessionStorage.setItem('sessionToken', session.sessionToken)

                if (session.sessionTuitionPayment) {
                    state.sessionTuitionPayment = session.sessionTuitionPayment
                    sessionStorage.setItem('sessionTuitionPayment', session.sessionTuitionPayment)
                }
            }
        },
        CHANGE_LOADING_STATUS(state, status) {
            state.loading = status
            if (!status) {
                state.loadingMessage = ''
            }
        },
        CHANGE_LOADING_MESSAGE(state, status) {
            state.loadingMessage = status
        },
        SET_LOCK_BACK(state, status) {
            state.lockBack = status
        },
        IS_TUITION_FEE_TREMS_AGREE(state, status) {
            state.isTuitionFeeAgreementChecked = status
        },
        IS_PUBLICBUILDING_FEE_TERMS_AGREE(state, status) {
            state.isPublicBuildingFeeAgreementChecked = status
        },
        SET_SHOW_FEATURE(state, isShowFeature) {
            state.supportFeatures = isShowFeature
            sessionStorage.setItem('showFeature', isShowFeature)
        },
        SET_LOCAL_TAX_LIST(state, list) {
            state.localTaxList = list
            sessionStorage.setItem('localTaxList', JSON.stringify(list))
        },
        SET_DEFAULT_CARRIER(state, data) {
            state.carrier = data
            sessionStorage.setItem('carrier', data)
        },
        SET_READY_TO_PAY_BILL(state, readyToPayBill) {
            state.readyToPayBill = readyToPayBill

            if (readyToPayBill === null || readyToPayBill === undefined) {
              sessionStorage.removeItem('readyToPayBill');
            } else {
              sessionStorage.setItem('readyToPayBill', JSON.stringify(readyToPayBill));
            }
        },
        SET_STUDENT_INFORMATION(state, studentInformation) {
            state.studentInformation = studentInformation
            sessionStorage.setItem('studentInformation', JSON.stringify(studentInformation))
        },
        SET_PENDING_STUDENT_ID_NUMBER(state, studentIdNumber) {
            state.studentIdNumber = studentIdNumber
            sessionStorage.setItem('studentIdNumber', studentIdNumber)
        },
    },
    actions: {
        sentApp({ commit }, data) {
            commit('SENT_APP', data)
        },
        getDeviceInformation({ commit }) {
            commit('GET_DEVICE_INFORMATION')
        },
        setSession({ commit }, session) {
            commit('SET_SESSION', session)
        },
        showLoading({ commit }) {
            commit('SHOW_LOADING')
        },
        changeLoadingStatus({ commit }, status) {
            commit('CHANGE_LOADING_STATUS', status)
            commit('SENT_APP', { action: 'isShowLoading', payload: { isShow: status } })
        },
        setLockBack({ commit }, status) {
            commit('SET_LOCK_BACK', status)
        },
        changeLoadingStatusWithMessage({ commit }, status) {
            commit('CHANGE_LOADING_STATUS', status.status)
            commit('CHANGE_LOADING_MESSAGE', status.message)
            commit('SENT_APP', { action: 'isShowLoading', payload: { isShow: status.status } })
        },
        isTuitionFeeTermsAgree({ commit }, status) {
            commit('IS_TUITION_FEE_TREMS_AGREE', status)
        },
        isPublicBuildingFeeTermsAgree({ commit }, status) {
            commit('IS_PUBLICBUILDING_FEE_TERMS_AGREE', status)
        },
        setSupportFeatures({ commit }, showFeature) {
            commit('SET_SHOW_FEATURE', showFeature)
        },
        setLocalTaxList({ commit }, list) {
            commit('SET_LOCAL_TAX_LIST', list)
        },
        setDefaultCarrier({ commit }, carrier) {
            commit('SET_DEFAULT_CARRIER', carrier)
        },
        setReadyToPayBill({ commit }, readyToPayBill) {
            commit('SET_READY_TO_PAY_BILL', readyToPayBill)
        },
        setStudentInformation({ commit }, studentInformation) {
            commit('SET_STUDENT_INFORMATION', studentInformation)
        },
        setPendingStudentIdNumber({ commit }, studentIdNumber) {
            commit('SET_PENDING_STUDENT_ID_NUMBER', studentIdNumber)
        },
    },
    getters: {
        deviceVersion: state => state.deviceVersion,
        device: state => state.device,
        sessionKey: state => state.sessionKey !== null ? state.sessionKey : sessionStorage.getItem('sessionKey'),
        sessionToken: state => state.sessionToken !== null ? state.sessionToken : sessionStorage.getItem('sessionToken'),
        sessionTuitionPayment: state => state.sessionTuitionPayment || sessionStorage.getItem('sessionTuitionPayment'),
        loading: state => state.loading,
        loadingMessage: state => state.loadingMessage,
        lockBack: state => state.lockBack,
        isTuitionFeeAgreementChecked: state => state.isTuitionFeeAgreementChecked,
        isPublicBuildingFeeAgreementChecked: state => state.isPublicBuildingFeeAgreementChecked,
        supportFeatures: state => state.supportFeatures !== null ? state.supportFeatures : sessionStorage.getItem('showFeature'),
        localTaxList: state => state.localTaxList !== null ? state.localTaxList : JSON.parse(sessionStorage.getItem('localTaxList')),
        carrier: state => state.carrier !== null ? state.carrier : sessionStorage.getItem('carrier'),
        readyToPayBill: state => state.readyToPayBill !== null ? state.readyToPayBill : JSON.parse(sessionStorage.getItem('readyToPayBill')),
        studentInformation: state => state.studentInformation !== null ? state.studentInformation : JSON.parse(sessionStorage.getItem('studentInformation')),
        pendingStudentIdNumber: state => state.studentIdNumber !== null ? state.studentIdNumber : sessionStorage.getItem('studentIdNumber'),
    }
})
